import { type ReactNode } from "react";

type BadgeProps = {
  /** Colour */
  tone?: "slate" | "green" | "amber" | "rose" | "sky" | "violet";
  /** Text */
  children: ReactNode;
};

const TONES: Record<NonNullable<BadgeProps["tone"]>, string> = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  sky: "bg-sky-50 text-sky-700 ring-sky-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
};

/** Small coloured label. */
export const Badge = ({ tone = "slate", children }: BadgeProps) => (
  <span
    className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-xs font-medium ring-1 ring-inset ${TONES[tone]}`}
  >
    {children}
  </span>
);

/** Badge tone of an HTTP status code. */
export const statusTone = (status: number): NonNullable<BadgeProps["tone"]> =>
  status >= 500
    ? "rose"
    : status >= 400
      ? "amber"
      : status >= 300
        ? "sky"
        : "green";

/** Badge tone of an HTTP method. */
export const methodTone = (method: string): NonNullable<BadgeProps["tone"]> =>
  (
    ({
      GET: "sky",
      POST: "green",
      PUT: "amber",
      PATCH: "amber",
      DELETE: "rose",
    }) as const
  )[method] ?? "violet";
